import { APIClient } from "./api_helper";
import * as url from "./url_helper";
import {
  ApiResponse,
  PaginatedInvoiceSummaryPayload,
  PaginatedPayload,
  PaginatedQuoteSummaryPayload,
} from "../types/ApiResponse";
import {
  CreateQuoteArgs,
  DeleteSectionApiArgs,
  getQuoteByIdParams,
  GetQuotePdfApiArgs,
  QuoteDetailsPayload,
  QuotesPayloadData,
  QuotesQueryParams,
  StatusPayload,
  UpdateQuoteArgs,
} from "types/quote.api";
import {
  CreateInvoiceArgs,
  GetInvoicePdfApiArgs,
  InvoiceDetailsPayload,
  InvoicePayloadData,
  InvoicesQueryParams,
  UpdateInvoiceArgs,
} from "types/invoice.api";
import {
  BusinessProfileSetupArgs,
  // BusinessProfileSetupArgs,
  CompanyPayload,
  ProfileArgs,
  // ProfileArgs,
  RegisterArgs,
  SupportTicketArgs,
  UserDetailPayload,
  UserPayload,
} from "types/auth.api";
// import { head } from "lodash";
import {
  ClientsPayloadData,
  ClientsQueryParams,
  AddClientFormParams,
  EditClientFormParams,
  ClientDetailsPayload,
} from "types/client.api";
import {
  DashboardApiPayload,
  AdminDashboardApiPayload,
  AdminHomeQueryParams,
} from "types/dashboard.api";
import {
  BillingPreferenceApiArgs,
  NotificationSettingsApiArgs,
  QuoteInvoiceSettingsApiArgs,
  UpdateStatusApiArgs,
} from "types/setting.api";
import {
  NotificationParams,
  NotificationPayloadData,
} from "types/notification.api";

import {
  CategoryData,
  GetCategoryListApiArgs,
  ManageCategoryArgs,
} from "types/category.api";

import {
  GetSubCategoryListApiArgs,
  ManageSubCategoryArgs,
  SubCategoryData,
} from "types/subcategory.api";
import {
  AddItemArgs,
  AddItemCSVArgs,
  EditItemArgs,
  GetItemListApiArgs,
  ItemData,
} from "types/item.api";
import { ConfigPayload } from "types/config.api";
import {
  BillingDetailArgs,
  BillingDetailsData,
} from "types/payment.invoicing.api";
import {
  CreatePaymentArgs,
  CreatePaymentPayload,
  PaymentDetailsPayload,
  SecurePaymentDetailsPayload,
  PaymentListPayload,
  PaymentParams,
  PaymentDepositQuotePayload,
  PaymentDepositQuotesQueryParams,
  PaymentsQueryParams,
  LinkPayload,
} from "types/payment.api";
import {
  AddSectionArgs,
  AddSectionsToQuoteArgs,
  DeleteSectionArgs,
  EditSectionArgs,
  GetSectionListApiArgs,
  SectionPayloadData,
} from "types/section.api";
import {
  AddTeamMemberArgs,
  EditTeamMemberArgs,
  GetTeamMembersApiArgs,
  TeamMember,
} from "types/team.api";
import { PresetQuoteArgs, PresetQuotePayload } from "types/preset.api";
import {
  AddressPayload,
  AdminUserListApiPayload,
  AdminUsersQueryParams,
} from "types/user.api";
import { ImpersonateUserApiPayload } from "types/impersonate.api";
import {
  BackendTourPayload,
  FinishTourPayload,
  ToursState,
} from "slices/tour/reducer";
import { TourKey } from "tour/tourKeys";
import { BillingInvoicesPayload } from "types/biling.api";
import {
  EmailInvoiceParams,
  EmailQuoteParams,
  EmailUpdateStatusParams,
  ViewInvoicePayload,
  ViewQuotePayload,
} from "types/email.api";

const api = new APIClient();

export const getTourGuideApi = (): Promise<ApiResponse<BackendTourPayload>> => {
  return api.create(url.GET_TOUR_GUIDE, {
    tour_type: "web",
  });
};

export const finishedTourApi = async (
  key: TourKey,
  tours: ToursState,
  tourType: "web" | "app" = "web",
) => {
  const user_guide = {} as Record<TourKey, { isFinished: boolean }>;

  (Object.keys(tours) as TourKey[]).forEach((tourKey) => {
    user_guide[tourKey] = {
      isFinished: tourKey === key ? true : tours[tourKey].isFinished,
    };
  });

  const payload: FinishTourPayload = {
    tour_type: tourType,
    user_guide,
  };

  console.log("finishedTourApi", payload);

  return api.create(url.POST_TOUR_GUIDE, {
    ...payload,
  });
};

export const getDashboardApi = (): Promise<
  ApiResponse<DashboardApiPayload>
> => {
  return api.get(url.GET_DASHBOARD);
};

export const getClientsApi = (
  params?: ClientsQueryParams,
): Promise<ApiResponse<PaginatedPayload<ClientsPayloadData>>> => {
  const queryParams = new URLSearchParams();
  if (params) {
    if (params.search) queryParams.append("search", params.search);
    if (params.page) queryParams.append("page", params.page.toString());
    if (params.sort_by)
      queryParams.append("sort_by", params.sort_by.toString());
  }
  const query = queryParams.toString();
  return api.get(query ? `${url.GET_CLIENTS}?${query}` : url.GET_CLIENTS);
};

export const getClientDetailsApi = (
  clientId: number,
): Promise<ApiResponse<ClientDetailsPayload>> => {
  return api.get(`${url.GET_CLIENT_BY_ID}/${clientId}`);
};

export const addNewClientApi = (
  data: AddClientFormParams,
): Promise<ApiResponse<ClientsPayloadData>> => {
  return api.create(url.ADD_NEW_CLIENT, data);
};

export const updateClientApi = (
  data: EditClientFormParams,
): Promise<ApiResponse<ClientsPayloadData>> => {
  return api.put<ApiResponse<ClientsPayloadData>>(
    `${url.UPDATE_CLIENT}/${data.id}`,
    data,
  );
};

export const deleteClientApi = (clientId: number) =>
  api.delete<ApiResponse<number>>(`${url.DELETE_CLIENT}/${clientId}`);

export const getQuotesApi = (
  params?: QuotesQueryParams,
): Promise<ApiResponse<PaginatedQuoteSummaryPayload<QuotesPayloadData>>> => {
  const queryParams = new URLSearchParams();

  if (params) {
    if (params.search) queryParams.append("search", params.search);
    if (params.page) queryParams.append("page", params.page.toString());
    if (params.client_id)
      queryParams.append("client_id", params.client_id.toString());
    if (params.start_date) queryParams.append("start_date", params.start_date);
    if (params.end_date) queryParams.append("end_date", params.end_date);

    if (params.sort_by) queryParams.append("sort_by", params.sort_by);
    if (params.sort_order) queryParams.append("sort_order", params.sort_order);

    if (params.status?.length) {
      params.status.forEach((s) => queryParams.append("status[]", s));
    }
  }

  const query = queryParams.toString();

  return api.get(query ? `${url.GET_QUOTES}?${query}` : url.GET_QUOTES);
};

export const getQuoteByIdApi = (quoteId: number) =>
  api.get<ApiResponse<QuoteDetailsPayload>>(
    `${url.GET_QUOTE_BY_ID}/${quoteId}`,
  );

export const getDuplicateQuoteByIdApi = (params: getQuoteByIdParams) =>
  api.create<ApiResponse<QuoteDetailsPayload>>(
    `${url.GET_QUOTE_BY_ID}/${params.quote_id}/duplicate`,
    {},
  );

export const createQuoteApi = (
  data: CreateQuoteArgs,
): Promise<ApiResponse<QuoteDetailsPayload>> => {
  const formData = new FormData();

  // Append text fields
  formData.append("title", data.title);
  formData.append("description", data.description);
  formData.append("notes", data.notes);
  formData.append("quote_date", data.quote_date);
  formData.append("expiry_date", data.expiry_date);
  formData.append("client_id", data.client_id.toString());

  // Append multiple attachments
  if (data.attachments && data.attachments.length > 0) {
    data.attachments.forEach((file, index) => {
      //@ts-ignore
      formData.append(`attachments[${index}]`, file);
    });
  }

  if (data.categorised) {
    formData.append("categorised", data.categorised);
  }
  if (data.template) {
    formData.append("template", data.template);
  }
  if (data.is_company_phone_number_show !== undefined) {
    formData.append(
      "is_company_phone_number_show",
      data.is_company_phone_number_show.toString(),
    );
  }

  return api.create(url.CREATE_QUOTE, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const deleteQuoteApi = (quoteId: number) =>
  api.delete<ApiResponse<number>>(`${url.DELETE_QUOTE}/${quoteId}`);

export const editQuoteApi = (
  data: UpdateQuoteArgs,
): Promise<ApiResponse<QuoteDetailsPayload>> => {
  const formData = new FormData();
  formData.append("_method", "put");

  if (data) {
    if (data.client_id) formData.append("client_id", data.client_id.toString());
    if (data.title) formData.append("title", data.title);
    if (data.description) formData.append("description", data.description);
    if (data.notes) formData.append("notes", data.notes);
    if (data.deposit_required)
      formData.append("deposit_required", data.deposit_required.toString());
    if (data.deposit_amount)
      formData.append("deposit_amount", data.deposit_amount.toString());
    if (data.deposit_type) formData.append("deposit_type", data.deposit_type);

    if (data.quote_date) formData.append("quote_date", data.quote_date);
    if (data.expiry_date) formData.append("expiry_date", data.expiry_date);
    if (data.vat_setting_id)
      formData.append("vat_setting_id", data.vat_setting_id.toString());
    if (data.discount) formData.append("discount", data.discount.toString());
    if (data.items) {
      data.items.forEach((item, index) => {
        formData.append(`items[${index}][id]`, item.id.toString());
        formData.append(`items[${index}][price]`, item.price.toString());
        formData.append(`items[${index}][quantity]`, item.quantity.toString());
        formData.append(`items[${index}][type]`, item.type);
        formData.append(`items[${index}][cost]`, item.cost.toString());
        formData.append(`items[${index}][unit]`, item.unit);
      });
    }
    if (data.status) formData.append("status", data.status);
    if (data.attachments && data.attachments.length > 0) {
      data.attachments.forEach((file, index) => {
        console.log("attachments", file, index);
        //@ts-ignore
        formData.append(`attachments[${index}]`, file);
      });
    }
    if (data.deposit_payment_method) {
      formData.append("deposit_payment_method", data.deposit_payment_method);
    }
    if (data.categorised) {
      formData.append("categorised", data.categorised);
    }
    if (data.template) {
      formData.append("template", data.template);
    }
    if (data.is_company_phone_number_show !== undefined) {
      formData.append(
        "is_company_phone_number_show",
        data.is_company_phone_number_show.toString(),
      );
    }
    if (data.deposit_percentage) {
      formData.append("deposit_percentage", data.deposit_percentage.toString());
    }
  }

  console.log("formData", JSON.stringify(formData, null, 2));

  return api.create(`${url.UPDATE_QUOTE}/${data.quote_id}`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const getQuotePdfApi = (params: GetQuotePdfApiArgs): Promise<any> => {
  const queryParams = new URLSearchParams();
  if (params.is_download)
    queryParams.append("is_download", params.is_download.toString());
  const query = queryParams.toString();
  return api.get(
    `${url.GENERATE_QUOTE}/${params.quoteId}/pdf${query ? `?${query}` : ""}`,
    {},
    params.is_download === 1 ? { responseType: "arraybuffer" } : undefined,
  );
};

export const sendQuoteEmailApi = (
  quoteId: number,
): Promise<ApiResponse<null>> => {
  return api.create<ApiResponse<null>>(
    `${url.SEND_QUOTE}/${quoteId}/send-email`,
    {},
  );
};

export const getInvoicesApi = (
  params?: InvoicesQueryParams,
): Promise<ApiResponse<PaginatedInvoiceSummaryPayload<InvoicePayloadData>>> => {
  const queryParams = new URLSearchParams();

  if (params) {
    if (params.search) queryParams.append("search", params.search);
    if (params.page) queryParams.append("page", params.page.toString());
    if (params.start_date) queryParams.append("start_date", params.start_date);
    if (params.end_date) queryParams.append("end_date", params.end_date);

    if (params.sort_by) queryParams.append("sort_by", params.sort_by);
    if (params.sort_order) queryParams.append("sort_order", params.sort_order);
    if (params.status) queryParams.append("status", params.status);
    if (params.quote_id)
      queryParams.append("quote_id", params.quote_id.toString());

    if (params.is_not_paid)
      queryParams.append("is_not_paid", params.is_not_paid.toString());
  }
  const query = queryParams.toString();
  return api.get(query ? `${url.GET_INVOICES}?${query}` : url.GET_INVOICES);
};

export const getInvoiceByIdApi = (invoiceId: number) =>
  api.get<ApiResponse<InvoiceDetailsPayload>>(
    `${url.GET_INVOICE_BY_ID}/${invoiceId}`,
  );

export const createInvoiceApi = (
  data: CreateInvoiceArgs,
): Promise<ApiResponse<InvoiceDetailsPayload>> => {
  const formData = new FormData();

  formData.append("quote_id", data.quote_id.toString());
  formData.append("invoice_date", data.invoice_date);
  formData.append("due_date", data.due_date);
  if (data.message) {
    formData.append("message", data.message);
  }
  if (data.notes) {
    formData.append("notes", data.notes);
  }

  // Append multiple attachments
  if (data.attachments && data.attachments.length > 0) {
    data.attachments.forEach((file, index) => {
      //@ts-ignore
      formData.append(`attachments[${index}]`, file);
    });
  }

  if (data.categorised) {
    formData.append("categorised", data.categorised);
  }
  if (data.template) {
    formData.append("template", data.template);
  }
  if (data.is_company_phone_number_show !== undefined) {
    formData.append(
      "is_company_phone_number_show",
      data.is_company_phone_number_show.toString(),
    );
  }

  return api.create(url.CREATE_INVOICE, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const editInvoiceApi = (
  data: UpdateInvoiceArgs,
): Promise<ApiResponse<InvoiceDetailsPayload>> => {
  const formData = new FormData();
  formData.append("_method", "put");

  if (data) {
    if (data.invoice_id)
      formData.append("invoice_id", data.invoice_id.toString());
    if (data.invoice_date) formData.append("invoice_date", data.invoice_date);
    if (data.due_date) formData.append("due_date", data.due_date);
    if (data.message) formData.append("message", data.message);

    if (data.notes) formData.append("notes", data.notes);
    if (data.discount) formData.append("discount", data.discount.toString());
    if (data.items) {
      data.items.forEach((item, index) => {
        formData.append(`items[${index}][id]`, item.id.toString());
        formData.append(`items[${index}][price]`, item.price.toString());
        formData.append(`items[${index}][quantity]`, item.quantity.toString());
        formData.append(`items[${index}][type]`, item.type);
        formData.append(`items[${index}][cost]`, item.cost.toString());
        formData.append(`items[${index}][unit]`, item.unit);
      });
    }
    if (data.attachments && data.attachments.length > 0) {
      data.attachments.forEach((file, index) => {
        console.log("attachments", file, index);
        //@ts-ignore
        formData.append(`attachments[${index}]`, file);
      });
    }

    if (data.categorised) {
      formData.append("categorised", data.categorised);
    }
    if (data.template) {
      formData.append("template", data.template);
    }
    if (data.is_company_phone_number_show !== undefined) {
      formData.append(
        "is_company_phone_number_show",
        data.is_company_phone_number_show.toString(),
      );
    }
    if (data.payment_method !== undefined) {
      formData.append("payment_method", data.payment_method ?? "");
    }
  }

  return api.create(`${url.UPDATE_INVOICE}/${data.invoice_id}`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const deleteInvoiceApi = (invoiceId: number) =>
  api.delete<ApiResponse<number>>(`${url.DELETE_INVOICE}/${invoiceId}`);

export const sendInvoiceEmailApi = (
  invoiceId: number,
): Promise<ApiResponse<null>> => {
  return api.create(`${url.SEND_INVOICE_EMAIL}/${invoiceId}/send-email`, {});
};

export const getInvoicePdfApi = (
  params: GetInvoicePdfApiArgs,
): Promise<any> => {
  const queryParams = new URLSearchParams();
  if (params.is_download)
    queryParams.append("is_download", params.is_download.toString());
  const query = queryParams.toString();
  return api.get(
    `${url.GENERATE_INVOICE}/${params.invoiceId}/pdf${query ? `?${query}` : ""}`,
    {},
    params.is_download === 1 ? { responseType: "arraybuffer" } : undefined,
  );
};

//Auth
export const registerUserApi = (
  data: RegisterArgs,
): Promise<ApiResponse<UserPayload>> => api.create(url.POST_REGISTER, data);

export const loginApi = (
  data: RegisterArgs,
): Promise<ApiResponse<UserPayload>> => api.create(url.POST_LOGIN, data);

export const profileSetupApi = (
  data: ProfileArgs,
): Promise<ApiResponse<UserPayload>> => {
  const formData = new FormData();
  formData.append("name", data?.name);
  formData.append("phone", data?.phone);
  formData.append("_method", data?._method);

  if (data.avatar) {
    formData.append("avatar", data?.avatar);
  }
  return api.create(url.POST_PROFILE, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};
export const logoutApi = () => api.create(url.POST_LOGOUT, {});
export const deleteApi = () => api.delete(url.DELETE_ACCOUNT, {});

export const resendVerificationEmailApi = () =>
  api.create(url.POST_RESEND_VERIFICATION_EMAIL, {});

export const forgotPasswordApi = (
  email: string,
): Promise<ApiResponse<UserPayload>> =>
  api.create(url.POST_PASSWORD_FORGET, { email });
////

export const businessProfileSetupApi = (
  data: BusinessProfileSetupArgs,
): Promise<ApiResponse<CompanyPayload>> => {
  const formData = new FormData();
  console.log("businessProfileSetupApi", data?.phone);

  formData.append("address", data?.address);
  // formData.append("state", data?.state);
  formData.append("country", data?.country);
  formData.append("city", data?.city);
  formData.append("postcode", data?.postcode);
  // if (data.vat_number) {
  formData.append("vat_number", data?.vat_number ?? "");
  if (data.vertical_market_id) {
    formData.append("vertical_market_id", data?.vertical_market_id.toString());
  }
  // }
  if (data.brand_color) {
    formData.append("brand_color", data?.brand_color);
  }
  // formData.append("email", data?.email);
  formData.append("name", data?.name);
  formData.append("phone", data?.phone);
  if (data.logo) {
    formData.append("logo", data?.logo);
  }
  console.log("formData entries:", Array.from(formData.entries()));

  if (data.isUpdate) {
    formData.append("_method", "put");
  }
  if (data.is_company_name_show !== undefined) {
    formData.append(
      "is_company_name_show",
      data.is_company_name_show.toString(),
    );
  }
  return api.create(url.POST_BUSINESS_PROFILE_SETUP, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};
export const changePasswordApi = (data: {
  old_password: string;
  new_password: string;
}): Promise<ApiResponse<null>> => {
  return api.create(url.CHANGE_PWD, data);
};

export const notificationSettingsApi = (
  data: NotificationSettingsApiArgs,
): Promise<ApiResponse<ConfigPayload>> => {
  return api.update(url.NOTIFICATION_SETTINGS, data);
};

export const quoteInvoiceSettingsApi = (
  data: QuoteInvoiceSettingsApiArgs,
): Promise<ApiResponse<ConfigPayload>> => {
  const formData = new FormData();
  if (data.footer_message) {
    formData.append("footer_message", data.footer_message);
  }
  if (data.terms_and_conditions) {
    formData.append("terms_and_conditions", data.terms_and_conditions);
  }
  if (data.signature) {
    formData.append("signature", data.signature);
  }
  return api.create(url.QUOTE_INVOICE_SETTINGS, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const billingPreferencesApi = (
  data: BillingPreferenceApiArgs,
): Promise<ApiResponse<ConfigPayload>> => {
  return api.create(url.BILLING_PREFERENCES, data);
};

export const updateStatusApi = (
  data: UpdateStatusApiArgs,
): Promise<ApiResponse<StatusPayload>> => {
  return api.update(url.UPDATE_STATUS, data);
};

//Notification API
export const getNotificationsApi = (
  params?: NotificationParams,
): Promise<ApiResponse<PaginatedPayload<NotificationPayloadData>>> => {
  return api.get<ApiResponse<PaginatedPayload<NotificationPayloadData>>>(
    url.GET_NOTIFICATION,
    params,
  );
};
// CATEGORY

export const getCategoryListApi = (
  params?: GetCategoryListApiArgs,
): Promise<ApiResponse<PaginatedPayload<CategoryData>>> => {
  const queryParams = new URLSearchParams();

  if (params) {
    if (params.search) queryParams.append("search", params.search);
    if (params.page) queryParams.append("page", params.page.toString());
  }
  const query = queryParams.toString();

  return api.get(query ? `${url.GET_CATEGORIES}?${query}` : url.GET_CATEGORIES);
};

export const manageCategoryApi = (
  data: ManageCategoryArgs,
): Promise<ApiResponse<CategoryData>> => {
  return api.create(url.MANAGE_CATEGORY, data);
};

export const deleteCategoryApi = (
  categoryId: number,
): Promise<ApiResponse<number>> =>
  api.delete(`${url.DELETE_CATEGORY}/${categoryId}`);

// SUB-CATEGORY

export const getSubCategoryListApi = (
  params?: GetSubCategoryListApiArgs,
): Promise<ApiResponse<PaginatedPayload<SubCategoryData>>> => {
  const queryParams = new URLSearchParams();

  if (params) {
    if (params.search) queryParams.append("search", params.search);
    if (params.page) queryParams.append("page", params.page.toString());
    if (params.category_id)
      queryParams.append("category_id", params.category_id.toString());
  }
  const query = queryParams.toString();

  return api.get(
    query ? `${url.GET_SUBCATEGORIES}?${query}` : url.GET_SUBCATEGORIES,
  );
};

export const manageSubCategoryApi = (
  data: ManageSubCategoryArgs,
): Promise<ApiResponse<SubCategoryData>> => {
  return api.create(url.MANAGE_SUBCATEGORY, data);
};

export const deleteSubCategoryApi = (
  subcategoryId: number,
): Promise<ApiResponse<number>> =>
  api.delete(`${url.DELETE_SUBCATEGORY}/${subcategoryId}`);

// ITEMS

export const getItemListApi = (
  params?: GetItemListApiArgs,
): Promise<ApiResponse<PaginatedPayload<ItemData>>> => {
  const queryParams = new URLSearchParams();
  if (params) {
    if (params.search) queryParams.append("search", params.search);
    if (params.category_id)
      queryParams.append("category_id", params.category_id.toString());
    if (params.invoice_id)
      queryParams.append("invoice_id", params.invoice_id.toString());
    if (params.quote_id)
      queryParams.append("quote_id", params.quote_id.toString());
    if (params.page) queryParams.append("page", params.page.toString());
    if (params.quote_template_id)
      queryParams.append(
        "quote_template_id",
        params.quote_template_id.toString(),
      );
    if (params?.subcategory_ids?.length) {
      params.subcategory_ids.forEach((id) => {
        queryParams.append("subcategory_ids[]", id.toString());
      });
    }
  }
  const query = queryParams.toString();
  return api.get(query ? `${url.GET_ITEMS}?${query}` : url.GET_ITEMS, {});
};

export const AddItemApi = (
  data: AddItemArgs,
): Promise<ApiResponse<ItemData>> => {
  console.log("add Args", data);
  return api.create(url.ADD_ITEM, data);
};

export const EditItemApi = (
  data: EditItemArgs,
): Promise<ApiResponse<ItemData>> => {
  return api.create(url.ADD_ITEM, data);
};

export const AddItemFromCSVApi = (
  data: AddItemCSVArgs,
): Promise<ApiResponse<PaginatedPayload<ItemData>>> => {
  const formData = new FormData();
  if (data.file) {
    formData.append("file", data.file);
  }
  return api.create(url.ADD_ITEM_CSV, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const deleteItemApi = (itemId: number): Promise<ApiResponse<number>> =>
  api.delete(`${url.DELETE_ITEM}/${itemId}`);

export const getConfigApi = (): Promise<ApiResponse<ConfigPayload>> => {
  return api.get(url.GET_APP_CONFIG);
};

export const getBillingDetailsApi = (): Promise<
  ApiResponse<BillingDetailsData>
> => {
  return api.get(url.BILLING_DETAILS);
};

export const manageBillingDetailsApi = (
  data: BillingDetailArgs,
): Promise<ApiResponse<BillingDetailsData>> => {
  return api.create(url.MANAGE_BILLING_DETAILS, data);
};

//section
export const getSectionListApi = (
  params?: GetSectionListApiArgs,
): Promise<ApiResponse<PaginatedPayload<SectionPayloadData>>> => {
  const queryParams = new URLSearchParams();
  if (params) {
    if (params.page) queryParams.append("page", params.page.toString());
    if (params.search) queryParams.append("search", params.search);
    if (params.quote_id)
      queryParams.append("quote_id", params.quote_id.toString());
  }
  const query = queryParams.toString();
  // return api.get(query ? `${url.GET_ITEMS}?${query}` : url.GET_ITEMS);
  return api.get<ApiResponse<PaginatedPayload<SectionPayloadData>>>(
    query ? `${url.GET_SECTIONS}?${query}` : `${url.GET_SECTIONS}`,
  );
};

export const getSectionByIdApi = (
  quoteId: number,
  page: number,
): Promise<ApiResponse<PaginatedPayload<SectionPayloadData>>> => {
  const queryParams = new URLSearchParams();
  if (page) {
    queryParams.append("page", page.toString());
  }
  const query = queryParams.toString();
  return api.get(
    query
      ? `quotes/${quoteId}/sections?${query}`
      : `quotes/${quoteId}/sections`,
  );
};
export const deleteSectionApi = (
  params: DeleteSectionArgs,
): Promise<ApiResponse<number>> =>
  api.delete(`${url.DELETE_SECTIONS}/${params.sectionId}`);

export const AddSectionApi = (
  data: AddSectionArgs,
): Promise<ApiResponse<SectionPayloadData>> => {
  return api.create(url.ADD_SECTION, data);
};

export const EditSectionApi = (
  data: EditSectionArgs,
): Promise<ApiResponse<SectionPayloadData>> => {
  return api.create(url.EDIT_SECTION, data);
};

export const AddSectionsToQuoteApi = (
  data: AddSectionsToQuoteArgs,
): Promise<ApiResponse<null>> => {
  return api.create(`quotes/${data.quote_id}/sections`, data);
};

//PRESET QUOTE TEMPLATE

export const getPresetQuoteListApi = (
  params: PresetQuoteArgs,
): Promise<ApiResponse<PaginatedPayload<PresetQuotePayload>>> => {
  const queryParams = new URLSearchParams();
  if (params) {
    if (params?.pageNo) queryParams.append("page", params?.pageNo?.toString());
    if (params.search) queryParams.append("search", params.search);
  }
  const query = queryParams.toString();

  return api.get<ApiResponse<PaginatedPayload<PresetQuotePayload>>>(
    query ? `${url.GET_PRESET_QUOTES}?${query}` : `${url.GET_PRESET_QUOTES}`,
  );
};

export const getPresetQuoteDetailApi = ({
  templateId,
}: {
  templateId: number;
}): Promise<ApiResponse<PresetQuotePayload>> => {
  return api.get(`${url.GET_PRESET_QUOTES}/${templateId}`);
};

export const createPresetQuoteTemplateApi = (
  data: Partial<PresetQuotePayload>,
): Promise<ApiResponse<PresetQuotePayload>> => {
  return api.create(url.POST_PRESET_QUOTES, data);
};

export const updatePresetQuoteTemplateApi = (
  data: Partial<PresetQuotePayload>,
): Promise<ApiResponse<PresetQuotePayload>> => {
  return api.create(url.POST_PRESET_QUOTES, data);
};

export const deletePresetQuoteTemplateApi = ({
  templateId,
}: {
  templateId: number;
}): Promise<ApiResponse<null>> => {
  return api.delete(`${url.DELETE_PRESET_QUOTES}/${templateId}`);
};

export const delteAttachmentApi = (
  param: DeleteSectionApiArgs,
): Promise<ApiResponse<number>> => {
  return api.delete(
    `quotes/${param.quote_id}/attachments/${param.attachment_id}`,
  );
};

//TEAM MEMBERS
export const getTeamMembersApi = (
  params?: GetTeamMembersApiArgs,
): Promise<ApiResponse<PaginatedPayload<TeamMember>>> => {
  const queryParams = new URLSearchParams();
  console.log(params);

  if (params) {
    if (params.search) queryParams.append("search", params.search);
    if (params.page) queryParams.append("page", params.page.toString());
  }
  const query = queryParams.toString();
  return api.get(
    query ? `${url.GET_TEAM_MEMBERS}?${query}` : url.GET_TEAM_MEMBERS,
  );
};

export const editTeamMemberApi = (
  data: EditTeamMemberArgs,
): Promise<ApiResponse<TeamMember>> => {
  return api.create(url.EDIT_TEAM_MEMBER, data);
};

export const addTeamMemberApi = (
  data: AddTeamMemberArgs,
): Promise<ApiResponse<TeamMember>> => {
  return api.create(url.ADD_TEAM_MEMBER, data);
};

//Submit Support Ticket
export const submitSupportTicketApi = (
  data: SupportTicketArgs,
): Promise<ApiResponse<null>> => {
  return api.create(url.SUBMIT_SUPPORT_TICKET, data);
};

//Admin Dashboard Apis
export const getAdminHomeApi = (
  params?: AdminHomeQueryParams,
): Promise<ApiResponse<AdminDashboardApiPayload>> => {
  const queryParams = new URLSearchParams();
  if (params) {
    if (params.start_date) queryParams.append("start_date", params.start_date);
    if (params.end_date) queryParams.append("end_date", params.end_date);
  }
  return api.get(url.ADMIN_HOME + `?${queryParams.toString()}`);
};

export const getAdminUsersListApi = (
  params?: AdminUsersQueryParams,
): Promise<ApiResponse<PaginatedPayload<AdminUserListApiPayload>>> => {
  const queryParams = new URLSearchParams();

  if (params) {
    if (params.page) queryParams.append("page", params.page.toString());
    if (params.per_page)
      queryParams.append("per_page", params.per_page.toString());
    if (params.search) queryParams.append("search", params.search);

    // Handle status array
    if (params.status?.length) {
      params.status.forEach((s) => queryParams.append("status[]", s));
    }

    // Handle subscription array
    if (params.subscription?.length) {
      params.subscription.forEach((s) =>
        queryParams.append("subscription[]", s),
      );
    }

    // Handle vertical array
    if (params.vertical?.length) {
      params.vertical.forEach((v) =>
        queryParams.append("vertical[]", v.toString()),
      );
    }
  }

  return api.get(url.ADMIN_USERS + `?${queryParams.toString()}`);
};

export const getUserDetailsApi = (): Promise<
  ApiResponse<UserDetailPayload>
> => {
  return api.get(url.GET_USER_DETAIL);
};

//Impersonate APIs
export const initiateImpersonateApi = (
  id: number,
): Promise<ApiResponse<ImpersonateUserApiPayload>> => {
  return api.create(`${url.IMPERSONATE_INITIATE}/${id}/impersonate`, {});
};

export const leaveImpersonateApi = (): Promise<
  ApiResponse<ImpersonateUserApiPayload>
> => {
  return api.create(url.IMPERSONATE_LEAVE, {});
};

export const manageDisableUserApi = (
  id: number,
): Promise<ApiResponse<null>> => {
  return api.update(`${url.MANAGE_DISABLE_USER}/${id}/activate-deactivate`, {});
};

export const adminDeleteUserApi = (
  userId: number,
): Promise<ApiResponse<null>> => {
  return api.create(url.ADMIN_DELETE_USER, { user_id: userId });
};

//Billing Api
export const getBillingApi = (params: {
  starting_after?: string | null;
}): Promise<ApiResponse<BillingInvoicesPayload>> => {
  const queryParams = new URLSearchParams();
  if (params) {
    if (params.starting_after)
      queryParams.append("starting_after", params.starting_after);
  }
  const query = queryParams.toString();
  return api.get<ApiResponse<BillingInvoicesPayload>>(
    query ? `${url.GET_BILLINGS}?${query}` : url.GET_BILLINGS,
  );
};

export const downloadInvoiceApi = (params: {
  pageUrl: string;
}): Promise<Blob> => {
  return api.create<Blob>(
    url.DOWNLOAD_BILLING_INVOICE,
    { invoice_url: params.pageUrl },
    {
      responseType: "blob",
    },
  );
};

export const getAddressApi = async (
  postalCode: string,
): Promise<ApiResponse<AddressPayload[]>> => {
  return api.get<ApiResponse<AddressPayload[]>>(
    `${url.GET_ADDRESS}=${postalCode}`,
  );
};

export const viewEmailQuoteApi = (
  params: EmailQuoteParams,
): Promise<ApiResponse<ViewQuotePayload>> => {
  const { quoteId, hash } = params;
  return api.create<ApiResponse<ViewQuotePayload>>(
    `${url.VIEW_EMAIL_QUOTE}/${quoteId}/secure/${hash}`,
    {},
  );
};

export const viewEmailInvoiceApi = (
  params: EmailInvoiceParams,
): Promise<ApiResponse<ViewInvoicePayload>> => {
  const { invoiceId, hash } = params;
  return api.create<ApiResponse<ViewInvoicePayload>>(
    `${url.VIEW_EMAIL_INVOICE}/${invoiceId}/secure/${hash}`,
    {},
  );
};

export const emailUpdateStatusApi = (
  data: EmailUpdateStatusParams,
): Promise<ApiResponse<null>> => {
  const { hash, status, quoteId, invoiceId } = data;
  console.log("emailUpdateStatusApi", data);

  return api.update<ApiResponse<null>>(
    `${url.EMAIL_UPDATE_STATUS}/${hash}`,
    quoteId
      ? {
        status,
        quote_id: quoteId,
      }
      : {
        status,
        invoice_id: invoiceId,
      },
  );
};

// PAYMENTS

export const getPaymentsApi = (
  params?: PaymentsQueryParams,
): Promise<ApiResponse<PaginatedPayload<PaymentListPayload>>> => {
  const queryParams = new URLSearchParams();
  if (params) {
    if (params.search) queryParams.append("search", params.search);
    if (params.page) queryParams.append("page", params.page.toString());
    if (params.client_id)
      queryParams.append("client_id", params.client_id.toString());
    if (params.invoice_id)
      queryParams.append("invoice_id", params.invoice_id.toString());
  }
  const query = queryParams.toString();
  return api.get(query ? `${url.PAYMENT}?${query}` : url.PAYMENT);
};

export const createPaymentApi = (
  data: CreatePaymentArgs,
): Promise<ApiResponse<CreatePaymentPayload>> => {
  const formData = new FormData();
  if (data.client_id !== undefined)
    formData.append("client_id", data.client_id.toString());
  if (data.invoice_id !== undefined)
    formData.append("invoice_id", data.invoice_id.toString());
  formData.append("amount_type", data.amount_type);
  formData.append("payment_method", data.payment_method);
  if (data.amount !== undefined)
    formData.append("amount", data.amount.toString());
  formData.append("currency", data.currency);
  formData.append("payment_date", data.payment_date);
  if (data.order_id) formData.append("order_id", data.order_id);
  return api.create(url.PAYMENT, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const deletePaymentApi = (
  paymentId: number,
): Promise<ApiResponse<number>> => {
  return api.delete(`${url.DELETE_PAYMENT}/${paymentId}`);
};

export const getPaymentDepositQuotesApi = (
  params: PaymentDepositQuotesQueryParams,
): Promise<ApiResponse<PaginatedPayload<PaymentDepositQuotePayload>>> => {
  const queryParams = new URLSearchParams();
  queryParams.append("client_id", params.client_id.toString());
  if (params.search) queryParams.append("search", params.search);
  if (params.page) queryParams.append("page", params.page.toString());

  return api.get(`${url.PAYMENT_DEPOSIT_QUOTES}?${queryParams.toString()}`);
};

export const sharePaymentApi = (
  params: PaymentParams,
): Promise<ApiResponse<LinkPayload>> => {
  return api.create(`${url.PAYMENT}/${params.id}/share`, {});
};

export const regeneratePaymentLinkApi = (
  params: PaymentParams,
): Promise<ApiResponse<LinkPayload>> => {
  return api.create(`${url.PAYMENT}/${params.id}/link`, {});
};

export const getPaymentDetailsApi = (
  params: PaymentParams,
): Promise<ApiResponse<PaymentDetailsPayload>> => {
  return api.get(`${url.PAYMENT_DETAILS}/${params.id}`);
};

export const getSecurePaymentDetailsApi = (params: {
  id: number;
  token: string;
}): Promise<ApiResponse<SecurePaymentDetailsPayload>> => {
  return api.create(`payment/${params.id}/secure/${params.token}`, {
  });
};

export const getStripeSetupLinkApi = (): Promise<
  ApiResponse<{ url: string }>
> => {
  return api.create(url.STRIPE_SETUP, {});
};
